import styled, { css } from 'styled-components';

import {
  ThemeProps as Prop, Components, FontFamily,
  FontSize,
  Breakpoints,
} from '@powdr/constants';
import { getFontStyles, themeUtil } from '@powdr/utils';

const CPNT = Components.WEARLYNX_LEADERBOARD;

export const StyledWearlynxLeaderboard = styled.div`
  position: relative;
  width: 100%;

  ${({ $isWrapperless }) => {
    if ($isWrapperless) return 'height: 100vh;';

    return 'height: 50vh';
  }};
`;

export const LeaderboardTable = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1;
`;

export const TableTitle = styled.h3`
  ${({ $isWrapperless }) => ($isWrapperless
    ? `
        padding: 25px 0;
        ${getFontStyles(FontFamily.PRIMARY_FONT, '50px', undefined, undefined)}
      `
    : `
      padding: 0 0 15px;
      ${getFontStyles(FontFamily.PRIMARY_FONT, FontSize.LRG10, undefined, undefined, Components.WYSIWYG, 'header3')};
    `)}
  text-align: center;
  color: ${({ $colorProfile }) => themeUtil(Prop.HEADER, CPNT, $colorProfile)};
  background-color: ${({ $colorProfile }) => themeUtil(Prop.BACKGROUND, CPNT, $colorProfile)};
`;

export const TableHeader = styled.div`
  display: flex;
  flex-direction: column;
  color: ${({ $colorProfile }) => themeUtil(Prop.HEADER, CPNT, $colorProfile)};
`;

export const TableBody = styled.div`
  height: 100%;
`;

export const ScrollableContainer = styled.div`
  overflow-y: auto;
  position: relative;
  z-index: 0;
  height: 100%;
  transition: opacity ${({ $fadeDuration }) => $fadeDuration}ms ease;
  opacity: ${({ $fading }) => ($fading ? 0 : 1)};

  ${({ $disableScroll }) => ($disableScroll) && `
    /* For WebKit browsers (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
    pointer-events: none;
  `}
`;

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  height: fit-content;

  &:nth-child(even) {
    background-color: #b3b3b3;
  }
`;

export const HeaderCell = styled.div`
  flex: 1;
  background-color: ${({ $colorProfile }) => themeUtil(Prop.BTN_BG, CPNT, $colorProfile)};
  color: ${({ $colorProfile }) => themeUtil(Prop.BTN_TXT, CPNT, $colorProfile)};
  ${({ $isWrapperless }) => (($isWrapperless) ? 'padding: 2%;' : 'padding: 25px 15px;')};
  ${({ $alignRight }) => $alignRight && 'text-align: right;'}
  ${({ $isWrapperless }) => getFontStyles(FontFamily.BASE_FONT, ($isWrapperless) ? FontSize.LRG30 : FontSize.MED30, undefined, undefined)};

  ${Breakpoints.mobile(css`
    &.stat-header {
      ${({ $isWrapperless }) => ($isWrapperless) && 'flex-grow: 2;'}
    }

    ${({ $isWrapperless }) => ($isWrapperless) && `
      ${getFontStyles(FontFamily.BASE_FONT, FontSize.LRG12, undefined, undefined)}
    `};
  `)}
`;

export const StatCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  text-align: right;
  height: 100%;
  ${({ $isWrapperless }) => getFontStyles(FontFamily.BASE_FONT, ($isWrapperless) ? FontSize.LRG30 : FontSize.MED30, undefined, undefined)};
  ${({ $isWrapperless }) => (($isWrapperless) ? 'padding: 2%;' : 'padding: 15px;')};

  ${Breakpoints.mobile(css`
    ${({ $isWrapperless }) => ($isWrapperless) && `
      ${getFontStyles(FontFamily.BASE_FONT, FontSize.MED50, undefined, undefined)}
    `};
  `)}
`;

export const NameCell = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 4%;
  padding: 2%;
  height: 100%;

  ${({ $isWrapperless }) => (($isWrapperless) ? 'gap: 4%; padding: 2%;' : 'gap: 20px; padding: 15px;')}

  ${Breakpoints.mobile(css`
    ${({ $isWrapperless }) => ($isWrapperless) && `
      flex-basis: 75%;
      gap: 3%;
    `};
  `)}
`;

export const Rank = styled.div`
  width: ${({ $isWrapperless }) => ($isWrapperless ? 75 : 35)}px;
  ${({ $isWrapperless }) => getFontStyles(FontFamily.PRIMARY_FONT, ($isWrapperless) ? FontSize.LRG30 : FontSize.MED30, undefined, undefined)};

  ${Breakpoints.mobile(css`
    ${({ $isWrapperless }) => ($isWrapperless) && `
      width: 35px;
      ${getFontStyles(FontFamily.PRIMARY_FONT, FontSize.MED50, undefined, undefined)}
    `};
  `)}
`;

export const NameProfilePicWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Name = styled.div`
  ${({ $isWrapperless }) => getFontStyles(FontFamily.BASE_FONT, ($isWrapperless) ? FontSize.LRG30 : FontSize.MED30, undefined, undefined)};

  ${Breakpoints.mobile(css`
    ${({ $isWrapperless }) => ($isWrapperless) && `
      ${getFontStyles(FontFamily.BASE_FONT, FontSize.MED50, undefined, undefined)}
    `};
  `)}
`;

export const ProfilePictureWrapper = styled.div`
  ${({ $isWrapperless }) => (($isWrapperless) ? 'width: 28vw;' : 'height: 75px')};
  aspect-ratio: 1/1;

  ${Breakpoints.mobile(css`
    ${({ $isWrapperless }) => ($isWrapperless) && `
      width: 15vw;
    `};
  `)}
`;

export const ProfilePicture = styled.img`
  border-radius: 50%;
  width: 100%;
  aspect-ratio: 1/1;
`;

export const ProfilePicturePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: ${({ $colorProfile }) => themeUtil(Prop.ELEMENT, CPNT, $colorProfile)};
  color: ${({ $colorProfile }) => themeUtil(Prop.ELEMENT_TXT, CPNT, $colorProfile)};
  border-radius: 50%;
  ${({ $isWrapperless }) => getFontStyles(FontFamily.PRIMARY_FONT, ($isWrapperless) ? '75px' : FontSize.MED30, undefined, undefined)};

  ${Breakpoints.mobile(css`
    ${({ $isWrapperless }) => ($isWrapperless) && getFontStyles(FontFamily.PRIMARY_FONT, '40px', undefined, undefined)};
  `)}
`;

export const EmptyLeaderboardMessage = styled.div`
  padding: 5%;
  width: 100%;
  height: 50%;
  text-align: center;
  ${({ $isWrapperless }) => getFontStyles(FontFamily.PRIMARY_FONT, ($isWrapperless) ? FontSize.LRG30 : FontSize.MED30, undefined, undefined)};
`;
